/**
 * 현재 시간과 비교하여 상대적인 시간을 반환합니다
 * @param date - 비교할 날짜 (UTC ISO 문자열)
 * @returns "방금 전", "3분 전", "1시간 전", "2일 전" 등의 문자열
 */

const TIMEZONE_SUFFIX_REGEX = /(Z|[+-]\d{2}:\d{2}|[+-]\d{4})$/i;

/**
 * 서버에서 내려오는 UTC 날짜 문자열을 안전하게 Date 객체로 변환합니다.
 * timezone suffix가 없으면 UTC로 간주해 Z를 붙입니다.
 */
export const parseServerUtcDate = (date: Date | string): Date => {
  if (date instanceof Date) {
    return date;
  }

  const normalizedDate = date.trim();
  const utcDateString = TIMEZONE_SUFFIX_REGEX.test(normalizedDate)
    ? normalizedDate
    : `${normalizedDate}Z`;

  return new Date(utcDateString);
};

export const getRelativeTime = (date: Date | string): string => {
  const targetDate = parseServerUtcDate(date);

  if (isNaN(targetDate.getTime())) {
    return "방금 전";
  }

  const now = new Date();
  const diffMs = now.getTime() - targetDate.getTime();

  if (diffMs < 0) {
    return "방금 전";
  }

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;

  if (diffMs < minute) {
    return "방금 전";
  } else if (diffMs < hour) {
    const minutes = Math.floor(diffMs / minute);
    return `${minutes}분 전`;
  } else if (diffMs < day) {
    const hours = Math.floor(diffMs / hour);
    return `${hours}시간 전`;
  } else if (diffMs < week) {
    const days = Math.floor(diffMs / day);
    return `${days}일 전`;
  } else if (diffMs < month) {
    const weeks = Math.floor(diffMs / week);
    return `${weeks}주 전`;
  } else if (diffMs < year) {
    const months = Math.floor(diffMs / month);
    return `${months}개월 전`;
  } else {
    const years = Math.floor(diffMs / year);
    return `${years}년 전`;
  }
};

export const getFormattedTimeWithDate = (date: Date | string) => {
  const targetDate = parseServerUtcDate(date);

  return targetDate.toLocaleString("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

/** KST 기준 "2026.09.28" 형태의 짧은 날짜 (Figma 표기). */
export const getFormattedDateCompact = (date: Date | string): string => {
  const targetDate = parseServerUtcDate(date);
  if (isNaN(targetDate.getTime())) return "";

  return targetDate
    .toLocaleDateString("ko-KR", {
      timeZone: "Asia/Seoul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\.\s*/g, ".")
    .replace(/\.$/, "");
};

/**
 * KST 기준 초까지 포함된 날짜-시간 포맷 문자열을 반환합니다.
 * 입력이 문자열인 경우 UTC ISO 문자열로 간주하며, 'Z'가 없으면 자동으로 붙입니다.
 */
export const getFormattedDateTimeKST = (date: Date | string): string => {
  const targetDate = parseServerUtcDate(date);
  if (isNaN(targetDate.getTime())) return "";

  return targetDate.toLocaleString("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
